import { useEffect, useMemo, useState } from "react";
import { Toaster } from "sonner";
import { HeroScreen } from "./components/HeroScreen";
import { QuizScreen } from "./components/QuizScreen";
import { EmailCaptureScreen } from "./components/EmailCaptureScreen";
import { ResultScreen } from "./components/ResultScreen";
import { questions, type Profile } from "./data/questions";
import { computeProfile } from "./lib/scoring";
import {
  loadState,
  saveState,
  clearState,
  type Screen,
  type PersistedState,
} from "./lib/storage";
import { hydrateContext } from "./lib/utm";
import { fetchLeadCount } from "./lib/supabase";
import { initAnalytics, trackEvent } from "./lib/analytics";

const VALID_REFS: Profile[] = ["visionnaire", "chill", "stratege", "ambitieux", "securitaire"];

function isProfile(s: string | undefined | null): s is Profile {
  return !!s && (VALID_REFS as string[]).includes(s);
}

export default function App() {
  const ctx = useMemo(() => hydrateContext(), []);
  const refProfile = isProfile(ctx.ref) ? ctx.ref : null;

  const [screen, setScreen] = useState<Screen>("hero");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [percentage, setPercentage] = useState<number | null>(null);
  const [liveCount, setLiveCount] = useState<number | null>(null);

  // Hydrate depuis localStorage (au tout premier render)
  useEffect(() => {
    initAnalytics();
    const persisted = loadState();
    if (persisted) {
      setScreen(persisted.screen);
      setCurrentQuestion(persisted.currentQuestion);
      setAnswers(persisted.answers);
      setProfile(persisted.profile);
      setPercentage(persisted.percentage);
    }
    fetchLeadCount().then(setLiveCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persiste à chaque changement
  useEffect(() => {
    const state: PersistedState = {
      screen,
      currentQuestion,
      answers,
      profile,
      percentage,
    };
    saveState(state);
  }, [screen, currentQuestion, answers, profile, percentage]);

  const handleStart = () => {
    setScreen("quiz");
    setCurrentQuestion(0);
    setAnswers([]);
    setProfile(null);
    setPercentage(null);
  };

  const handleAnswerChange = (qIdx: number, aIdx: number) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[qIdx] = aIdx;
      return next;
    });
  };

  const handleQuizComplete = (finalAnswers: number[]) => {
    const result = computeProfile(finalAnswers);
    setAnswers(finalAnswers);
    setProfile(result.profile);
    setPercentage(result.percentage);
    trackEvent("quiz_complete", { profile: result.profile, percentage: result.percentage });
    setScreen("email");
  };

  const handleEmailDone = () => {
    setScreen("result");
  };

  const handleRestart = () => {
    clearState();
    setScreen("hero");
    setCurrentQuestion(0);
    setAnswers([]);
    setProfile(null);
    setPercentage(null);
  };

  // Recalcule le profil si on a des réponses mais pas de profil (ex: ouvert direct sur /result via persist invalide)
  useEffect(() => {
    if ((screen === "email" || screen === "result") && (!profile || percentage == null) && answers.length === questions.length) {
      const result = computeProfile(answers);
      setProfile(result.profile);
      setPercentage(result.percentage);
    }
  }, [screen, profile, percentage, answers]);

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "Comfortaa, sans-serif",
            borderRadius: "1rem",
          },
        }}
      />
      <div key={screen}>
        {screen === "hero" && (
          <HeroScreen
            onStart={handleStart}
            refProfile={refProfile}
            liveCount={liveCount}
          />
        )}
        {screen === "quiz" && (
          <QuizScreen
            currentIndex={currentQuestion}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            onIndexChange={setCurrentQuestion}
            onComplete={handleQuizComplete}
          />
        )}
        {screen === "email" && profile && percentage !== null && (
          <EmailCaptureScreen
            profile={profile}
            percentage={percentage}
            answers={answers}
            context={ctx}
            onDone={handleEmailDone}
          />
        )}
        {screen === "result" && profile && percentage !== null && (
          <ResultScreen
            profile={profile}
            percentage={percentage}
            onRestart={handleRestart}
          />
        )}
      </div>
    </>
  );
}
